// Admin Categories Page

import {
	useState,
	useEffect,
	useCallback,
	useDispatch,
	useSelector,
	useRProductCategoriesMutation,
	useAdminListTrashedCategoriesMutation,
	useAdminCreateCategoryMutation,
} from "custom_exports/all_hooks";
import {
	Box,
	Typography,
	Button,
	TextField,
	Stack,
	Paper,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Chip,
	Avatar,
	InputAdornment,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormControlLabel,
} from "custom_exports/material_ui_items";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageIcon from "@mui/icons-material/Image";
import {
	adminCategoryActions,
	productCategoryActions,
	getAdminCategoryItemsAsList,
	getProductCategoryItemsAsList,
} from "services/redux/reducers/slice";
import { callApi } from "common/api_handlers/api_call_manager";
import K from "common/keywords";
import Swal from "sweetalert2";
import { THEME } from "app/settings/settings";

function AdminCategoriesPage() {
	//--------------------------------- variables ---------------------------------//
	const dispatch = useDispatch();
	const { setAdminCategories, addAdminCategory } = adminCategoryActions;
	const { setProductCategory } = productCategoryActions;

	const adminCategoriesAsList = useSelector(getAdminCategoryItemsAsList);
	const liveCategoriesAsList = useSelector(getProductCategoryItemsAsList);

	const [searchQuery, setSearchQuery] = useState("");
	const [viewMode, setViewMode] = useState("live"); // "live" or "trashed"
	const [dialogOpen, setDialogOpen] = useState(false);
	const [formData, setFormData] = useState({
		title: "",
		parent_id: "",
		is_physical: true,
		priority: 1000,
	});
	const [imageFile, setImageFile] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const [RProductCategoriesApiCaller] = useRProductCategoriesMutation();
	const [adminListTrashedApiCaller] = useAdminListTrashedCategoriesMutation();
	const [adminCreateApiCaller] = useAdminCreateCategoryMutation();

	//--------------------------------- functions ---------------------------------//
	const onApiResponse = ({ apiCallerId, ...response }) => {
		const { data } = response;
		if (apiCallerId === K.adminListTrashedCategories) {
			dispatch(setAdminCategories(data.product_categories.items));
		} else if (apiCallerId === K.adminCreateCategory) {
			dispatch(addAdminCategory(data.product_categories.item));
			refreshLiveCategories();
			Swal.fire({ text: "Category created successfully", icon: "success", confirmButtonColor: THEME.app.colors.primary.main });
			handleCloseDialog();
		}
		setIsLoading(false);
	};

	const onApiError = ({ apiCallerId, ...error }) => {
		console.log(error);
		Swal.fire({ text: error.errorMessage || "Something went wrong", icon: "error", confirmButtonColor: THEME.app.colors.red.main });
		setIsLoading(false);
	};

	const apiActions = { onApiResponse, onApiError };

	const refreshLiveCategories = () => {
		callApi({
			apiCallerId: K.RProductCategories,
			apiCaller: RProductCategoriesApiCaller,
			apiInputParams: {},
			apiActions: {
				onApiResponse: ({ data }) => dispatch(setProductCategory(data.product_categories.items)),
				onApiError: (err) => console.log(err),
			},
		});
	};

	const loadTrashedCategories = useCallback(() => {
		setIsLoading(true);
		callApi({
			apiCallerId: K.adminListTrashedCategories,
			apiCaller: adminListTrashedApiCaller,
			apiInputParams: {},
			apiActions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ---- handlers ---- //
	const handleOpenCreateDialog = () => {
		setFormData({ title: "", parent_id: "", is_physical: true, priority: 1000 });
		setImageFile(null);
		setImagePreview(null);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setFormData({ title: "", parent_id: "", is_physical: true, priority: 1000 });
		setImageFile(null);
		setImagePreview(null);
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setImageFile(file);
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const handleSubmit = () => {
		if (!formData.title.trim()) {
			Swal.fire({ text: "Title is required", icon: "warning", confirmButtonColor: THEME.app.colors.primary.main });
			return;
		}

		setIsLoading(true);

		if (imageFile) {
			const createFormData = new FormData();
			createFormData.append("title", formData.title);
			if (formData.parent_id) createFormData.append("parent_id", formData.parent_id);
			createFormData.append("is_physical", formData.is_physical);
			createFormData.append("priority", formData.priority);
			createFormData.append("image", imageFile);
			callApi({
				apiCallerId: K.adminCreateCategory,
				apiCaller: adminCreateApiCaller,
				apiInputParams: createFormData,
				apiActions,
			});
		} else {
			const params = { title: formData.title, is_physical: formData.is_physical, priority: formData.priority };
			if (formData.parent_id) params.parent_id = formData.parent_id;
			callApi({
				apiCallerId: K.adminCreateCategory,
				apiCaller: adminCreateApiCaller,
				apiInputParams: params,
				apiActions,
			});
		}
	};

	// ---- filtering ---- //
	const getDisplayCategories = useCallback(() => {
		const sourceList = viewMode === "trashed" ? adminCategoriesAsList : liveCategoriesAsList;
		if (!searchQuery.trim()) return sourceList;
		const query = searchQuery.toLowerCase();
		return sourceList.filter((c) => c.title.toLowerCase().includes(query));
	}, [viewMode, adminCategoriesAsList, liveCategoriesAsList, searchQuery]);

	// ---- useEffects ---- //
	useEffect(() => {
		if (liveCategoriesAsList.length <= 0) {
			refreshLiveCategories();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (viewMode === "trashed") {
			loadTrashedCategories();
		}
	}, [viewMode, loadTrashedCategories]);

	//--------------------------------- contents ----------------------------------//
	const displayCategories = getDisplayCategories();

	const content = (
		<Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 2, sm: 3 } }}>
			{/* Header */}
			<Box sx={{ bgcolor: "white", borderRadius: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", p: 2, px: { xs: 2, sm: 3 }, mb: 3 }}>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField
							size="small"
							sx={{
								width: { xs: "100%", sm: "300px" },
								"& .MuiOutlinedInput-root": {
									borderRadius: "20px", bgcolor: "#f1f5f9",
									"& fieldset": { border: "none" },
									"&.Mui-focused fieldset": { border: "2px solid #6bc637" },
								},
							}}
							placeholder="Search categories..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "grey.500" }} /></InputAdornment> }}
						/>
						<Stack direction="row" spacing={1}>
							<Chip
								label="Live"
								onClick={() => setViewMode("live")}
								color={viewMode === "live" ? "success" : "default"}
								variant={viewMode === "live" ? "filled" : "outlined"}
							/>
							<Chip
								label="Trashed"
								onClick={() => setViewMode("trashed")}
								color={viewMode === "trashed" ? "error" : "default"}
								variant={viewMode === "trashed" ? "filled" : "outlined"}
							/>
						</Stack>
					</Stack>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleOpenCreateDialog}
						sx={{ borderRadius: "20px", bgcolor: "#6bc637", "&:hover": { bgcolor: "#5ab02e" }, textTransform: "none", fontWeight: 600 }}
					>
						Add Category
					</Button>
				</Stack>
			</Box>

			{/* Categories List */}
			{isLoading && viewMode === "trashed" ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: "#6bc637" }} /></Box>
			) : displayCategories.length === 0 ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<Typography color="text.secondary">{viewMode === "trashed" ? "No trashed categories" : "No categories found"}</Typography>
				</Box>
			) : (
				<Stack spacing={2}>
					{displayCategories.map((category) => (
						<Paper
							key={category.id}
							sx={{ p: 2, borderRadius: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 2 }}
						>
							<Avatar
								src={category.image || ""}
								variant="rounded"
								sx={{ width: 56, height: 56, bgcolor: "grey.100" }}
							>
								{!category.image && <ImageIcon sx={{ color: "grey.400" }} />}
							</Avatar>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography fontWeight={600} noWrap>{category.title}</Typography>
								<Stack direction="row" spacing={1} mt={0.5}>
									<Chip
										label={category.is_physical ? "Physical" : "Service"}
										size="small"
										color={category.is_physical ? "primary" : "secondary"}
										sx={{ fontSize: "0.7rem", height: 20 }}
									/>
									{category.parent && (
										<Chip label={`Parent: ${category.parent}`} size="small" sx={{ fontSize: "0.7rem", height: 20 }} />
									)}
									<Chip label={`Priority: ${category.priority ?? "—"}`} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 20 }} />
								</Stack>
							</Box>
						</Paper>
					))}
				</Stack>
			)}

			{/* Create Dialog */}
			<Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>Create Category</DialogTitle>
				<DialogContent>
					<Stack spacing={2.5} sx={{ mt: 1 }}>
						<TextField
							label="Category Title"
							fullWidth
							value={formData.title}
							onChange={(e) => setFormData({ ...formData, title: e.target.value })}
							inputProps={{ maxLength: 200 }}
						/>
						<FormControl fullWidth>
							<InputLabel>Parent Category (optional)</InputLabel>
							<Select
								value={formData.parent_id}
								onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
								label="Parent Category (optional)"
							>
								<MenuItem value="">None (Top Level)</MenuItem>
								{liveCategoriesAsList.map((cat) => (
									<MenuItem key={cat.id} value={cat.id}>{cat.title}</MenuItem>
								))}
							</Select>
						</FormControl>
						<FormControlLabel
							control={
								<Switch
									checked={formData.is_physical}
									onChange={(e) => setFormData({ ...formData, is_physical: e.target.checked })}
									color="success"
								/>
							}
							label={formData.is_physical ? "Physical Product" : "Service"}
						/>
						<TextField
							label="Priority"
							fullWidth
							type="number"
							value={formData.priority}
							onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
							helperText="Lower number = higher priority"
						/>
						{/* Image Upload */}
						<Box>
							<Typography variant="body2" color="text.secondary" mb={1}>Category Image</Typography>
							<Button variant="outlined" component="label" sx={{ borderRadius: "12px", textTransform: "none" }}>
								Choose Image
								<input type="file" accept="image/*" hidden onChange={handleImageChange} />
							</Button>
							{imagePreview && (
								<Box sx={{ mt: 1.5, width: 100, height: 100, borderRadius: "12px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
									<img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
								</Box>
							)}
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 0 }}>
					<Button onClick={handleCloseDialog} sx={{ borderRadius: "12px", textTransform: "none", color: "grey.600" }}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={isLoading}
						sx={{ borderRadius: "12px", bgcolor: "#6bc637", "&:hover": { bgcolor: "#5ab02e" }, textTransform: "none", fontWeight: 600 }}
					>
						{isLoading ? <CircularProgress size={24} /> : "Create"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);

	return content;
}

export default AdminCategoriesPage;
